import type { ReactNode } from 'react';
import { Stack } from '@understory-io/leaf/stack';
import { css } from '@understory-io/leaf/css';
import { createIntl, type MessageDescriptor } from 'react-intl';
import { cx } from '~/styled-system/css';
import type { RootLoaderData } from '~/root';

/**
 * Shared, presentational building blocks for the checkout steps — the content
 * wrapper, card styles, and step `<title>` helper. Kept out of the layout route
 * so the route file holds only the layout itself. None of these depend on the
 * checkout layout's loader, so there's no coupling back to the route.
 */

export { CheckoutCard, checkoutCardPadding } from './components/checkout-card';

const CHECKOUT_MAX_WIDTH = '[536px]';
// The info step's two-column layout (form + summary sidebar) needs more room
// than the single-column date steps (US-9853).
const CHECKOUT_WIDE_MAX_WIDTH = '[946px]';

const checkoutMainWidth = {
  default: css({ maxWidth: { base: 'full', tablet: CHECKOUT_MAX_WIDTH } }),
  wide: css({ maxWidth: { base: 'full', tablet: CHECKOUT_WIDE_MAX_WIDTH } }),
};

export function CheckoutMain({
  children,
  className,
  width = 'default',
}: {
  children: ReactNode;
  className?: string;
  /** Content max-width. `wide` (946px) is used by the info step's two-column
   *  layout; the date steps use `default` (536px). */
  width?: 'default' | 'wide';
}) {
  return (
    <div
      className={cx(
        css({
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          width: 'full',
          marginInline: 'auto',
          // Consistent gap after the Header on tablet+, matching the rest of
          // the storefront (PageCard); mobile spacing is handled per step
          // (US-9869).
          paddingTop: { base: 'none', tablet: '2xlarge' },
        }),
        checkoutMainWidth[width],
        className
      )}
    >
      <Stack className={css({ flexGrow: '1' })}>{children}</Stack>
    </div>
  );
}

/**
 * Builds an HTML `<title>` for a checkout step. Formats the step label in the
 * tenant's locale and appends the company name (e.g. "Tickets & Time — ACME").
 * Returns `[]` if the root loader data isn't available.
 */
export function buildCheckoutMetaTitle({
  matches,
  stepLabel,
}: {
  matches: Array<{ id: string; loaderData: unknown } | undefined>;
  stepLabel: MessageDescriptor;
}): Array<{ title: string }> {
  const root = matches.find((m) => m?.id === 'root')?.loaderData as
    RootLoaderData | undefined;
  if (!root) return [];
  const intl = createIntl({
    locale: root.locale,
    messages: root.translationMessages,
  });
  const stepTitle = intl.formatMessage(stepLabel);
  const title = root.companyName
    ? `${stepTitle} - ${root.companyName}`
    : stepTitle;
  return [{ title }];
}
